import { Fragment, type ReactNode } from 'react';
import type { DashboardSummary, DashboardWidgetId } from '@reform-lite/contracts';
import { StatTile } from '@/components/ui/stat-tile';
import { useDashboardSummary } from '@/hooks/use-dashboard';
import { formatMinor } from '@/hooks/use-shifts';
import { useT } from '@/i18n/use-t';

/**
 * RL-377 — the BOS dashboard.
 *
 * Two questions in one screen: is anything broken, and is money coming in.
 * Every tile shows one number and opens one page that already exists, with the
 * drill-down filter held in the URL (Q3), so refresh, sharing and browser Back
 * all land on the same view.
 *
 * The rule that shapes the whole file: a block MISSING from the response means
 * "not yours to see, or not licensed" and renders nothing at all — no empty
 * tile, no error. A block PRESENT with zeros means asked-and-answered and
 * renders its zeros. So every tile below is guarded on the block's presence and
 * never on its value.
 */

type Translate = (key: string, bakedEnglish?: string) => string;

/**
 * Money arrives as a decimal minor-unit string and must never be routed through
 * a float on its way to the screen. `formatMinor` does it with BigInt; the
 * float version in `hooks/use-receipts.ts` is RL-363 and is not reused here.
 *
 * `formatMinor` knows the € and ₴ glyphs only, so anything else keeps its code
 * alongside the digits, and a null currency (no receipts, or a window whose
 * receipts disagree) prints bare rather than claiming euros.
 */
function money(minor: string, currency: string | null): string {
  const formatted = formatMinor(minor, currency ?? '');
  if (currency === null || currency === 'EUR' || currency === 'UAH') return formatted;
  return `${formatted} ${currency}`;
}

function fill(template: string, values: Record<string, string>): string {
  return Object.entries(values).reduce(
    (acc, [name, value]) => acc.split(`{${name}}`).join(value),
    template,
  );
}

function href(path: string, query: Record<string, string>): string {
  return `${path}?${new URLSearchParams(query).toString()}`;
}

/** Receipt list bounds are inclusive, while the dashboard day end is exclusive. */
function inclusiveEnd(exclusiveIso: string): string {
  return new Date(new Date(exclusiveIso).getTime() - 1).toISOString();
}

/**
 * Tile titles are the widget picker's names, by design: one concept, one key.
 * The picker owns `dashboardSettings.widget.<id>.name` for every id in
 * `DASHBOARD_WIDGETS`, and reading the same key here is what stops the tile and
 * the picker drifting apart in a locale. The keys are literals rather than
 * built from the id at runtime, because the catalogue parity guard reads source.
 */
const WIDGET_NAME_KEY = {
  sales: 'dashboardSettings.widget.sales.name',
  posOnline: 'dashboardSettings.widget.posOnline.name',
  syncHealth: 'dashboardSettings.widget.syncHealth.name',
  openShifts: 'dashboardSettings.widget.openShifts.name',
  cashVariance: 'dashboardSettings.widget.cashVariance.name',
  approvals: 'dashboardSettings.widget.approvals.name',
  payments: 'dashboardSettings.widget.payments.name',
  kitchenOrders: 'dashboardSettings.widget.kitchenOrders.name',
  weightCoverage: 'dashboardSettings.widget.weightCoverage.name',
  fiscalHolds: 'dashboardSettings.widget.fiscalHolds.name',
} as const;

export function DashboardPage() {
  const t = useT('admin_ui');
  const { data, isLoading, error } = useDashboardSummary();

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">{t('dashboard.title', 'Dashboard')}</h1>
        <p className="text-sm text-muted-foreground">
          {t('dashboard.subtitle', 'Is anything broken, and is money coming in.')}
        </p>
        {data !== undefined && <WindowLine summary={data} t={t} />}
      </header>

      {isLoading && (
        <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>
      )}

      {error !== null && data === undefined && (
        <p className="text-sm text-destructive" data-testid="dashboard-error">
          {t('dashboard.loadFailed', 'Could not load the dashboard.')}
        </p>
      )}

      {error !== null && data !== undefined && (
        <p
          className="rounded-md border border-destructive p-3 text-sm text-destructive"
          data-testid="dashboard-stale"
        >
          {t(
            'dashboard.refreshFailed',
            'Dashboard refresh failed. The figures below are from the last successful update and may be stale.',
          )}
        </p>
      )}

      {data !== undefined && <Tiles summary={data} t={t} />}
    </div>
  );
}

/**
 * The resolved day window, said out loud. There is no tenant-level timezone in
 * the schema, so the server resolves one from the tenant's locations and tells
 * us which — and when an estate declares more than one, it falls back to UTC.
 * Showing somebody's midnight silently is the failure this line prevents.
 */
function WindowLine({ summary, t }: { summary: DashboardSummary; t: Translate }) {
  const w = summary.window;
  return (
    <div className="space-y-1 pt-1">
      <p className="text-xs text-muted-foreground" data-testid="dashboard-window">
        {fill(t('dashboard.window', 'Trading day {date} · {zone}'), {
          date: w.date,
          zone: w.timeZone,
        })}
        {' · '}
        {fill(t('dashboard.updated', 'Updated {time}'), {
          time: new Date(summary.generatedAt).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          }),
        })}
      </p>
      {w.ambiguous && (
        <p
          className="text-xs text-amber-600 dark:text-amber-400"
          data-testid="dashboard-window-ambiguous"
        >
          {t(
            'dashboard.windowAmbiguous',
            'Locations declare more than one timezone, so the day was computed in UTC.',
          )}
        </p>
      )}
    </div>
  );
}

function Tiles({ summary, t }: { summary: DashboardSummary; t: Translate }) {
  const {
    window: w,
    sales,
    terminals,
    shifts,
    sync,
    cash,
    approvals,
    payments,
    kitchen,
    weight,
    fiscalHolds,
  } = summary;

  /**
   * Cash variance carries no currency of its own; when the day traded, its
   * receipts settle what the tenant counts in, and otherwise the figure prints
   * bare rather than guessing.
   */
  const currency = sales?.currency ?? null;

  const tiles: Record<DashboardWidgetId, ReactNode> = {
    sales:
      sales === undefined ? null : (
        <StatTile
          testId="dashboard-tile-sales"
          label={t(WIDGET_NAME_KEY.sales, 'Sales today')}
          value={
            sales.netMinor === null
              ? t(
                  sales.moneyUnavailable === 'mixed_currency'
                    ? 'dashboard.tile.mixedCurrency'
                    : 'dashboard.tile.unknownCurrency',
                  sales.moneyUnavailable === 'mixed_currency'
                    ? 'Mixed currencies — no combined total'
                    : 'Currency unknown — no total',
                )
              : money(sales.netMinor, sales.currency)
          }
          highlight
          to={href('/receipts', {
            finalisedFrom: w.from,
            finalisedTo: inclusiveEnd(w.to),
          })}
          hint={
            <>
              {sales.grossMinor !== null && sales.returnedMinor !== null && (
                <div>
                  {fill(
                    t('dashboard.tile.salesHint', 'Net = gross {gross} less returns {returned}'),
                    {
                      gross: money(sales.grossMinor, sales.currency),
                      returned: money(sales.returnedMinor, sales.currency),
                    },
                  )}
                </div>
              )}
              {/* MICRO-43: only an estate that took deposits sees the line. */}
              {sales.depositsCollectedMinor !== null &&
                sales.depositsRefundedMinor !== null &&
                (sales.depositsCollectedMinor !== '0' || sales.depositsRefundedMinor !== '0') && (
                  <div>
                    {fill(
                      t(
                        'dashboard.tile.depositsHint',
                        'Deposits {collected} collected · {refunded} refunded',
                      ),
                      {
                        collected: money(sales.depositsCollectedMinor, sales.currency),
                        refunded: money(sales.depositsRefundedMinor, sales.currency),
                      },
                    )}
                  </div>
                )}
              <div>
                {fill(t('dashboard.tile.salesCounts', '{receipts} receipts · {returns} returns'), {
                  receipts: String(sales.receiptCount),
                  returns: String(sales.returnCount),
                })}
              </div>
            </>
          }
        />
      ),
    posOnline:
      terminals === undefined ? null : (
        <StatTile
          testId="dashboard-tile-pos-online"
          label={t(WIDGET_NAME_KEY.posOnline, 'POS online')}
          value={`${terminals.online} / ${terminals.total}`}
          to={href('/terminals', { kind: 'pos', online: 'false' })}
          hint={fill(t('dashboard.tile.posOnlineHint', '{offline} offline'), {
            offline: String(terminals.offline),
          })}
        />
      ),
    syncHealth:
      sync === undefined ? null : (
        <StatTile
          testId="dashboard-tile-sync-health"
          label={t(WIDGET_NAME_KEY.syncHealth, 'Sync health')}
          value={String(sync.yellow + sync.red + sync.unknown)}
          to="/terminals"
          hint={fill(
            t(
              'dashboard.tile.syncHealthHint',
              '{green} green · {yellow} yellow · {red} red · {unknown} unknown',
            ),
            {
              green: String(sync.green),
              yellow: String(sync.yellow),
              red: String(sync.red),
              unknown: String(sync.unknown),
            },
          )}
        />
      ),
    openShifts:
      shifts === undefined ? null : (
        <StatTile
          testId="dashboard-tile-open-shifts"
          label={t(WIDGET_NAME_KEY.openShifts, 'Open shifts')}
          value={String(shifts.open)}
          to={href('/shifts', { state: 'trading' })}
          hint={fill(t('dashboard.tile.openShiftsHint', '{abnormal} closed abnormally'), {
            abnormal: String(shifts.abnormal),
          })}
        />
      ),
    cashVariance:
      cash === undefined ? null : (
        <StatTile
          testId="dashboard-tile-cash-variance"
          label={t(WIDGET_NAME_KEY.cashVariance, 'Cash variance today')}
          value={money(cash.varianceMinor, currency)}
          to={href('/reports/turnover', { date: w.date, from: w.from, to: w.to })}
          hint={fill(t('dashboard.tile.cashVarianceHint', 'across {count} shifts'), {
            count: String(cash.shiftCount),
          })}
        />
      ),
    approvals:
      approvals === undefined ? null : (
        <StatTile
          testId="dashboard-tile-approvals"
          label={t(WIDGET_NAME_KEY.approvals, 'Pending approvals')}
          value={String(approvals.pending)}
          to="/approvals"
        />
      ),
    payments:
      payments === undefined ? null : (
        <StatTile
          testId="dashboard-tile-payments"
          label={t(WIDGET_NAME_KEY.payments, 'Stuck payments')}
          value={String(payments.stuck)}
          to="/payments/reconciliation"
        />
      ),
    kitchenOrders:
      kitchen === undefined ? null : (
        <StatTile
          testId="dashboard-tile-kitchen-orders"
          label={t(WIDGET_NAME_KEY.kitchenOrders, 'Kitchen orders')}
          value={String(kitchen.activeOrders)}
          to="/terminals/kitchen-orders"
          hint={fill(t('dashboard.tile.kitchenOrdersHint', 'across {count} locations'), {
            count: String(kitchen.byLocation.length),
          })}
        />
      ),
    weightCoverage:
      weight === undefined ? null : (
        <StatTile
          testId="dashboard-tile-weight-coverage"
          label={t(WIDGET_NAME_KEY.weightCoverage, 'Weight coverage')}
          value={`${weight.coveragePercent}%`}
          to="/weight/coverage"
        />
      ),
    // EAT-25: a held sale blocks its lane; the ones past their automatic
    // retries need a person, so the tile is marked while any is waiting.
    fiscalHolds:
      fiscalHolds === undefined ? null : (
        <StatTile
          testId="dashboard-tile-fiscal-holds"
          label={t(WIDGET_NAME_KEY.fiscalHolds, 'Held fiscal sales')}
          value={String(fiscalHolds.held)}
          to="/terminals/fiscal-holds"
          className={fiscalHolds.autoRetriesExhausted > 0 ? 'border-destructive' : undefined}
          hint={fill(
            t(
              'dashboard.tile.fiscalHoldsHint',
              '{exhausted} need a decision · {stale} on lanes not reporting',
            ),
            {
              exhausted: String(fiscalHolds.autoRetriesExhausted),
              stale: String(fiscalHolds.stale),
            },
          )}
        />
      ),
  };

  const rendered = summary.widgets.filter((widget) => tiles[widget] !== null);

  if (rendered.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="dashboard-empty">
        {t('dashboard.empty', 'No dashboard blocks are available to you.')}
      </p>
    );
  }

  return (
    <section className="grid grid-cols-2 gap-4 sm:grid-cols-5">
      {rendered.map((widget) => (
        <Fragment key={widget}>{tiles[widget]}</Fragment>
      ))}
    </section>
  );
}
